import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import BorderIcon from '@atlaskit/icon/core/border';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import MinimizeIcon from '@atlaskit/icon/core/minimize';
import { Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ColorPicker, useBuiltInColorMap } from '@atlassian/color-picker';
import { CollapsibleItem, useProductDependencies } from '@atlassian/company-hub-common';
import { CornerStyleConfig } from '@atlassian/company-hub-common/config-panel';

import type { LinksMenuExtensionActions } from '../../../../controllers/use-links-menu-parameters-and-actions';
import { BORDER, CORNERS, type LinksMenuParameters } from '../../../../types';

import { messages } from './messages';

const styles = cssMap({
	borderOptionContainer: {
		paddingBlock: token('space.025'),
		paddingInline: token('space.050'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	dropdownTrigger: {
		backgroundColor: token('color.background.neutral.subtle'),
	},
	border: {
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
	},
	selected: {
		backgroundColor: token('color.background.selected'),
		borderColor: token('color.border.accent.blue'),
	},
});

export type BackgroundConfigProps = Pick<
	LinksMenuParameters['styles'],
	'border' | 'borderColor' | 'cornerStyle' | 'fillColor'
> &
	Pick<
		LinksMenuExtensionActions,
		'updateBorder' | 'updateBorderColor' | 'updateCornerStyle' | 'updateFillColor'
	> & {
		isReadableFillColor: (color: string) => boolean;
	};

// TODO: COMPHUB-3164 - replace this background color field with the reusable one once it's implemented. Refer to ticket for more context
export const BackgroundConfig: React.FC<BackgroundConfigProps> = ({
	border,
	updateBorder,
	borderColor,
	updateBorderColor,
	fillColor,
	updateFillColor,
	cornerStyle,
	updateCornerStyle,
	isReadableFillColor,
}) => {
	const { formatMessage } = useIntl();
	const { isSSRProductInDarkMode } = useProductDependencies();
	const { branding } = useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap } = branding ?? {};

	const { backgroundColorMap, borderColorMap, emptyColorMap } =
		useBuiltInColorMap(isSSRProductInDarkMode);

	const handleBorderColorChange = (color: string) => {
		if (border === BORDER.NONE) {
			// if user interacts with border color then they need a border
			// position in order to see their changes
			updateBorder(BORDER.ALL_SIDES);
		}
		updateBorderColor(color);
	};

	const handleBorderOptionClick = (borderOption: BORDER) => updateBorder(borderOption);

	return (
		<CollapsibleItem title={formatMessage(messages.backgroundTitle)}>
			<Stack space="space.200">
				<Inline alignBlock="center" spread="space-between">
					<Text color="color.text">
						<FormattedMessage {...messages.backgroundColor} />
					</Text>
					<ColorPicker
						analyticsSource="linksMenuExtensionConfigBackgroundColor"
						builtInColorMap={
							hideDefaultColorPalette && brandBackgroundColorMap
								? brandBackgroundColorMap
								: backgroundColorMap
						}
						isReadableColor={isReadableFillColor}
						onChange={updateFillColor}
						value={fillColor}
						pickerTitle={messages.backgroundColor}
						showCustomPalette
					/>
				</Inline>
				<Inline alignBlock="center" spread="space-between">
					<Text>
						<FormattedMessage {...messages.borderPosition} />
					</Text>
					<Inline space="space.100">
						{[
							{
								borderOption: BORDER.NONE,
								label: messages.noBorderLabel,
								IconComponent: EyeOpenStrikethroughIcon,
							},
							{
								borderOption: BORDER.ALL_SIDES,
								label: messages.allBordersLabel,
								IconComponent: BorderIcon,
							},
							{
								borderOption: BORDER.BOTTOM,
								label: messages.bottomBorderLabel,
								IconComponent: MinimizeIcon,
							},
						].map(({ borderOption, label, IconComponent }) => {
							return (
								<Tooltip key={borderOption} content={formatMessage(label)}>
									<Pressable
										xcss={cx(
											styles.borderOptionContainer,
											styles.border,
											border === borderOption && styles.selected,
										)}
										onClick={() => handleBorderOptionClick(borderOption)}
										testId={`border-${borderOption}${border === borderOption ? '--selected' : ''}`}
									>
										<IconComponent
											color={
												border === borderOption
													? token('color.icon.accent.blue')
													: token('color.icon')
											}
											label={formatMessage(label)}
										/>
									</Pressable>
								</Tooltip>
							);
						})}
					</Inline>
				</Inline>
				<Inline alignBlock="center" spread="space-between">
					<Text color="color.text">
						<FormattedMessage {...messages.borderColor} />
					</Text>
					<ColorPicker
						analyticsSource="linksMenuExtensionConfigBorderColor"
						builtInColorMap={hideDefaultColorPalette ? emptyColorMap : borderColorMap}
						onChange={(color) => color && handleBorderColorChange(color)}
						value={borderColor}
						pickerTitle={messages.borderColor}
						showCustomPalette
					/>
				</Inline>
				<CornerStyleConfig<BackgroundConfigProps['cornerStyle']>
					cornerStyleValue={cornerStyle}
					updateCornerStyle={updateCornerStyle}
					roundCornerValue={CORNERS.ROUND}
					squareCornerValue={CORNERS.SHARP}
				/>
			</Stack>
		</CollapsibleItem>
	);
};
